import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { UserActions } from '@recruitee/account/data';
import { CandidateActions } from '@recruitee/candidate/data';
import { createEffectFactory, referencesToActionsFactory } from '@recruitee/ngrx';

import { GdprRequestHttpClient, GdprRequestReferences } from './gdpr-request-http-client';
import { actions, candidateScope, GdprRequestActions } from './gdpr-request.store';

@Injectable({ providedIn: 'root' })
export class GdprRequestEffects {
  public effect = createEffectFactory(actions, this.actions$);

  public fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.fetchCollection),
      mergeMap(({ payload: { candidateId } }) =>
        this.gdprRequestHttpClient.fetchCollection(candidateId).pipe(
          switchMap(({ requests, references }) => [
            ...this.getReferencesActions(references),
            actions.fetchCollectionSuccess({
              payload: { data: requests, scope: candidateScope(candidateId) },
            }),
          ]),
          catchError(error => of(actions.fetchCollectionFailed({ payload: { error } }))),
        ),
      ),
    ),
  );

  public saveGdprConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.complete),
      mergeMap(({ payload }) =>
        this.gdprRequestHttpClient.complete(payload.requestId).pipe(
          switchMap(({ request, references }) => [
            ...this.getReferencesActions(references),
            actions.completeSuccess({ payload: { data: request } }),
          ]),
          catchError(error => of(actions.completeFailed({ payload: { error } }))),
        ),
      ),
    ),
  );

  public reloadCollectionOnComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GdprRequestActions.completeSuccess),
      switchMap(
        ({
          payload: {
            data: { candidateId },
          },
        }) => [GdprRequestActions.fetchCollection({ payload: { candidateId } })],
      ),
    ),
  );

  private getReferencesActions = referencesToActionsFactory<GdprRequestReferences>()([
    { type: 'Admin', action: UserActions.references },
    { type: 'Candidate', action: CandidateActions.references },
  ]);

  constructor(private gdprRequestHttpClient: GdprRequestHttpClient, private actions$: Actions) {}
}
